import Booking, {
  NumberOfPersonsInSegment,
  NumberOfPersonsInClassification,
} from '@v/bookings/edit/models/Booking.js'
import Product from '@v/bookings/edit/models/Product.js'
import { mapSearchParamsToBookingProducts } from './util/mapSearchParamsToBookingProducts.js'
import Result from 'folktale/result'
import { fromNullable } from 'folktale/maybe'
import {
  any,
  propSatisfies,
  isEmpty,
  set,
  lensProp,
  lensPath,
  over,
  pipe,
  prop,
  map,
  filter,
  sortBy,
  chain,
  find,
  propEq,
} from 'ramda'
import formatApiError from '@v/util/formatApiError.js'
import { completeQuantities } from '@v/bookings/edit/util/completeBookingProductSegmentQuantities.js'

// todo: maybe refactor some awaits with Tasks
export default class BookingUpsertService {
  getResultHandler(searchParams) {
    return (products, classifications) => result => {
      return result
        .chain(this.JoinBookingProductsFromQueryParams(products, searchParams))
        .map(v => v.map(this.JoinClassifications(products, classifications)))
    }
  }

  JoinBookingProductsFromQueryParams(products, searchParams) {
    return value => {
      const validation = mapSearchParamsToBookingProducts({
        searchParams,
        products,
        booking: value.booking,
      }).map(newBookingProducts => {
        value.booking.BookingProducts =
          value.booking.BookingProducts?.concat(newBookingProducts) || newBookingProducts
        if (!value.booking.Key) {
          const firstScheduled = value.booking.BookingProducts.find(bp => !bp.NeedsToBeScheduled)
          value.booking.StartTime = firstScheduled?.FromDateTimeUTC
        }
        return value
      })
      return Result.Ok(validation)
    }
  }

  JoinClassifications(products, classifications) {
    return value => {
      const completeSegmentsPipe = pipe(
        completeSegments(classifications),
        completeSegmentQuantities(products, classifications), // mutations
      )
      return over(lensProp('booking'), completeSegmentsPipe, value)
    }
  }

  // todo: make separate services for fetch and upsert

  async upsert(upsertModel) {
    const bookingKey = upsertModel.key
    const updateCall = () => this.apiClient.put(`bookings/${bookingKey}/update`, upsertModel)
    const createCall = () => this.apiClient.post('bookings/create', upsertModel)
    const callParam = bookingKey
      ? { call: updateCall, successMessage: 'Booking saved' }
      : { call: createCall, successMessage: 'Booking created' }
    return await this.save(callParam)
  }

  async save({ call, successMessage }) {
    try {
      const response = await call()
      return Result.Ok({ response, successMessage })
    } catch (e) {
      const messages = this.handleSaveError(e)
      return Result.Error(messages)
    }
  }

  handleSaveError(e) {
    if (e.response?.status === 400 && e.response?.data?.message === '"EmailAddressInUse"') {
      return {
        errorMessage: null,
        userMessage: 'Customer email is in use',
      }
    }
    return formatApiError(e, 'Could not save booking')
  }
}

// ============ PRIVATE FUNCTIONS ============

// todo: unify handleEditResponse and handleCreateResponse

function newValidateResponse({ data, withBooking }) {
  const error = newGetResponseError({ data, withBooking }) // side effects
  return error ? Result.Error(error) : Result.Ok(data)
}

function newGetResponseError({ data, withBooking }) {
  if (!data) return 'data does not exist'
  if (!withBooking && !data.countries) console.warn('Response contains no countries') // todo: handle elsewhere
  if (withBooking && !data.booking) return 'booking does not exist'
}

// todo: reconsider this logic: if no matching product is found isSegmentless will become undefined
// should it not depend on the segments in the bookingproduct itself?
const mapBookingProducts = products => v =>
  map(bp => {
    const product = find(propEq('Key', bp.productKey))(products)
    return product ? { ...bp, isSegmentless: !product.ActiveSegments?.length } : bp
  })

function buildBooking(value) {
  return {
    ...value,
    booking: new Booking(value.booking),
  }
}

// === completing the booking model ===

/**
 * @typedef {Object} Segment
 * @property {string} code
 * @property {string} namePlural
 * @property {boolean} enabled
 */

/**
 * @typedef {Object} Classification
 * @property {string} code
 * @property {Segment[]} segments
 */

/**
 * Used to make sure all segments for a BU end up in NumberOfPersonsPerClassification.
 * @type {(classifications: Classification[]) => (booking: Booking) => Booking}
 */
const completeSegments = classifications => booking => {
  const isNewBooking = !booking.Key
  return isNewBooking
    ? set(
        lensProp('NumberOfPersonsPerClassification'),
        newCreatePersonsPerClassification(classifications),
        booking,
      )
    : booking
}

/**
 * Create numberOfPersonsPerClassification for a new booking with enabled segments set to 0 people
 * @type {(classifications: Classification[]) => NumberOfPersonsInClassification[]}
 */
const newCreatePersonsPerClassification = classifications =>
  classifications.map(c => ({
    ClassificationCode: c.Code,
    NumberOfPersonsPerSegment: c.Segments.filter(s => s.Enabled).map(s => ({
      SegmentCode: s.Code,
      NumberOfPersons: 0,
      SegmentNamePlural: s.NamePlural,
    })),
  }))

const anyWithSegments = any(propSatisfies(x => !isEmpty(x), 'NumberOfPersonsPerSegment'))

/**
 * Makes sure all active segments on a product end up in bookingproduct quantitiesPerClassification
 * @param {Array} products
 * @param {Array} classifications
 * @returns {(b: Booking) => Booking}
 */
const completeSegmentQuantities = (products, classifications) => booking => {
  const flatSegments = classifications.reduce((agg, next) => agg.concat(next.Segments), [])
  for (const bookingProduct of booking.BookingProducts) {
    const product = products.find(p => p.Key === bookingProduct.ProductKey)
    if (product) {
      bookingProduct.QuantitiesPerClassification = completeQuantities(
        product,
        flatSegments,
        bookingProduct,
      )
    } // mutations
  }
  return booking
}
